/**
 * AdminSettingsPage — Sprint Apr-30 2026 (Squad H) + LOC-D Field Tracking
 *
 * Tenant-level settings: working hours, sales defaults, discount approvals,
 * activity tracking, role permissions, field location tracking.
 *
 * GET  /api/v1/admin/tenant-config   — loads current merged config
 * PUT  /api/v1/admin/tenant-config   — saves partial config (deep-merge on server)
 * GET  /api/v1/auth/roles            — dynamic role list for multi-select
 *
 * Design: AURA design tokens only (var(--saffron), var(--ink-2), etc.).
 * No bg-primary-* Tailwind classes.
 */

import { useEffect, useState } from 'react';
import {
  Clock,
  Settings2,
  Percent,
  Activity,
  Shield,
  Save,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  MapPin,
} from 'lucide-react';
import { api } from '../../utils/api';

// ─── Types (mirror backend TenantConfig) ──────────────────────────────────────

type DayOfWeek = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

interface CompanyWorkingHours {
  days: DayOfWeek[];
  start: string;
  end: string;
  tz: string;
}

interface TenantConfig {
  maxMissedFollowUps: number;
  activityInactivityMinutes: number;
  paymentConfirmationRequired: boolean;
  discountApprovalDelegateToAccounts: boolean;
  additionalDiscountMaxPct: number;
  companyWorkingHours: CompanyWorkingHours;
  roleSettings: {
    teamHead: { canSkipPaymentConfirmation: boolean };
    accountsHead: { canApproveDiscounts: boolean };
  };
  // ── Field Location Tracking (LOC-A keys)
  locationTrackingEnabled: boolean;
  locationPingIntervalMinutes: number;
  locationTrackingActiveHoursOnly: boolean;
  locationTrackingRoles: string[];
  locationStationaryRadiusMeters: number;
}

// Fallback role list if /auth/roles API is unavailable
const FALLBACK_ROLES = [
  'admin',
  'owner',
  'SALES_MANAGER',
  'SALES_EXECUTIVE',
  'TELECALLER',
  'PRODUCTION_HEAD',
  'PRODUCTION_OPERATOR',
  'WAREHOUSE_EXECUTIVE',
  'ACCOUNTS_EXECUTIVE',
  'SERVICE_TECH',
  'FIELD_PARTNER',
];

const DEFAULT_CONFIG: TenantConfig = {
  maxMissedFollowUps: 5,
  activityInactivityMinutes: 3,
  paymentConfirmationRequired: true,
  discountApprovalDelegateToAccounts: false,
  additionalDiscountMaxPct: 7,
  companyWorkingHours: {
    days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'],
    start: '09:00',
    end: '18:00',
    tz: 'Asia/Kolkata',
  },
  roleSettings: {
    teamHead: { canSkipPaymentConfirmation: false },
    accountsHead: { canApproveDiscounts: false },
  },
  // Field Location Tracking defaults
  locationTrackingEnabled: false,
  locationPingIntervalMinutes: 15,
  locationTrackingActiveHoursOnly: true,
  locationTrackingRoles: [],
  locationStationaryRadiusMeters: 75,
};

const PING_INTERVAL_OPTIONS = [5, 10, 15, 20, 30, 60];

const ALL_DAYS: { key: DayOfWeek; label: string }[] = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const TIMEZONES = [
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Dubai',
  'UTC',
  'America/New_York',
  'Europe/London',
];

// ─── Small reusable pieces ────────────────────────────────────────────────────

function SectionCard({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div
      className="rounded-xl border"
      style={{
        background: 'var(--ivory-1)',
        borderColor: 'var(--line)',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left"
        style={{ color: 'var(--ink-1)' }}
      >
        <span
          className="flex items-center justify-center w-8 h-8 rounded-lg"
          style={{ background: 'var(--saffron-10, rgba(255,153,0,0.12))' }}
        >
          <Icon size={16} style={{ color: 'var(--saffron)' }} />
        </span>
        <span className="font-semibold text-sm flex-1">{title}</span>
        {open ? (
          <ChevronDown size={16} style={{ color: 'var(--ink-3)' }} />
        ) : (
          <ChevronRight size={16} style={{ color: 'var(--ink-3)' }} />
        )}
      </button>
      {open && (
        <div
          className="px-5 pb-5 border-t"
          style={{ borderColor: 'var(--line)' }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function FieldRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b last:border-b-0" style={{ borderColor: 'var(--line)' }}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium" style={{ color: 'var(--ink-1)' }}>{label}</p>
        {hint && <p className="text-xs mt-0.5" style={{ color: 'var(--ink-3)' }}>{hint}</p>}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

function Toggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      className="relative inline-flex w-11 h-6 items-center rounded-full transition-colors"
      style={{
        background: value ? 'var(--saffron)' : 'var(--line)',
      }}
    >
      <span
        className="inline-block w-4 h-4 rounded-full bg-white shadow transition-transform"
        style={{ transform: value ? 'translateX(22px)' : 'translateX(4px)' }}
      />
    </button>
  );
}

function NumberInput({
  value,
  onChange,
  min,
  max,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={e => onChange(Number(e.target.value))}
      className="w-20 rounded-lg border px-2 py-1.5 text-sm text-right"
      style={{
        borderColor: 'var(--line)',
        background: 'var(--ivory-0)',
        color: 'var(--ink-1)',
      }}
    />
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AdminSettingsPage() {
  const [config, setConfig] = useState<TenantConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableRoles, setAvailableRoles] = useState<string[]>(FALLBACK_ROLES);
  // Track original loc config to detect changes for the per-section save button
  const [locSaved, setLocSaved] = useState(false);
  const [locSaving, setLocSaving] = useState(false);

  // ── Load
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [configRes, rolesRes] = await Promise.allSettled([
          api.get('/admin/tenant-config'),
          api.get('/auth/roles'),
        ]);
        if (configRes.status === 'fulfilled' && configRes.value.data?.success) {
          setConfig({ ...DEFAULT_CONFIG, ...configRes.value.data.data });
        }
        if (rolesRes.status === 'fulfilled' && rolesRes.value.data?.data?.length) {
          setAvailableRoles(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rolesRes.value.data.data.map((r: any) => r.name as string)
          );
        }
      } catch (e: any) {
        setError(e?.response?.data?.error ?? (e as Error).message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Save
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const res = await api.put('/admin/tenant-config', config);
      if (res.data?.success && res.data?.data) {
        setConfig({ ...DEFAULT_CONFIG, ...res.data.data });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // ── Location-only save (sends just the 5 loc keys)
  const handleLocSave = async () => {
    try {
      setLocSaving(true);
      setError(null);
      const payload = {
        locationTrackingEnabled: config.locationTrackingEnabled,
        locationPingIntervalMinutes: config.locationPingIntervalMinutes,
        locationTrackingActiveHoursOnly: config.locationTrackingActiveHoursOnly,
        locationTrackingRoles: config.locationTrackingRoles,
        locationStationaryRadiusMeters: config.locationStationaryRadiusMeters,
      };
      const res = await api.put('/admin/tenant-config', payload);
      if (res.data?.success && res.data?.data) {
        setConfig(c => ({ ...c, ...res.data.data }));
      }
      setLocSaved(true);
      setTimeout(() => setLocSaved(false), 3000);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? (e as Error).message);
    } finally {
      setLocSaving(false);
    }
  };

  // ── Helpers
  const setLoc = (patch: Partial<Pick<TenantConfig,
    'locationTrackingEnabled' | 'locationPingIntervalMinutes' |
    'locationTrackingActiveHoursOnly' | 'locationTrackingRoles' |
    'locationStationaryRadiusMeters'>>) =>
    setConfig(c => ({ ...c, ...patch }));

  const toggleLocRole = (role: string) => {
    const roles = config.locationTrackingRoles;
    setLoc({
      locationTrackingRoles: roles.includes(role)
        ? roles.filter(r => r !== role)
        : [...roles, role],
    });
  };

  const setWorkingHours = (patch: Partial<CompanyWorkingHours>) =>
    setConfig(c => ({ ...c, companyWorkingHours: { ...c.companyWorkingHours, ...patch } }));

  const toggleDay = (day: DayOfWeek) => {
    const days = config.companyWorkingHours.days;
    setWorkingHours({
      days: days.includes(day) ? days.filter(d => d !== day) : [...days, day],
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--saffron)' }} />
      </div>
    );
  }

  return (
    <div className="aura-root max-w-3xl mx-auto px-4 pb-16 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Fraunces, serif', color: 'var(--ink-0)' }}>
            AURA Settings
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--ink-3)' }}>
            Configure tenant-wide defaults for sales, attendance, and permissions.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-opacity disabled:opacity-60"
          style={{ background: 'var(--saffron)' }}
        >
          {saving ? (
            <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
          ) : saved ? (
            <CheckCircle2 size={16} />
          ) : (
            <Save size={16} />
          )}
          {saving ? 'Saving…' : saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg px-4 py-3 mb-4 text-sm" style={{ background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FCA5A5' }}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="space-y-4">
        {/* ── 1. Working Hours ─────────────────────────────────────────────── */}
        <SectionCard icon={Clock} title="Working Hours">
          <div className="pt-4 space-y-0">
            <FieldRow label="Office Days" hint="Days when the SLA clock ticks and follow-up reminders fire.">
              <div className="flex gap-1.5 flex-wrap justify-end">
                {ALL_DAYS.map(({ key, label }) => {
                  const active = config.companyWorkingHours.days.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleDay(key)}
                      className="w-9 h-9 rounded-lg text-xs font-semibold transition-colors"
                      style={{
                        background: active ? 'var(--saffron)' : 'var(--ivory-2, #F3EFE8)',
                        color: active ? 'white' : 'var(--ink-2)',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </FieldRow>

            <FieldRow label="Office Hours" hint="HH:mm — 24-hour format">
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={config.companyWorkingHours.start}
                  onChange={e => setWorkingHours({ start: e.target.value })}
                  className="rounded-lg border px-2 py-1.5 text-sm"
                  style={{ borderColor: 'var(--line)', background: 'var(--ivory-0)', color: 'var(--ink-1)' }}
                />
                <span className="text-xs" style={{ color: 'var(--ink-3)' }}>to</span>
                <input
                  type="time"
                  value={config.companyWorkingHours.end}
                  onChange={e => setWorkingHours({ end: e.target.value })}
                  className="rounded-lg border px-2 py-1.5 text-sm"
                  style={{ borderColor: 'var(--line)', background: 'var(--ivory-0)', color: 'var(--ink-1)' }}
                />
              </div>
            </FieldRow>

            <FieldRow label="Timezone">
              <select
                value={config.companyWorkingHours.tz}
                onChange={e => setWorkingHours({ tz: e.target.value })}
                className="rounded-lg border px-2 py-1.5 text-sm"
                style={{ borderColor: 'var(--line)', background: 'var(--ivory-0)', color: 'var(--ink-1)' }}
              >
                {TIMEZONES.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </FieldRow>
          </div>
        </SectionCard>

        {/* ── 2. Sales Defaults ─────────────────────────────────────────────── */}
        <SectionCard icon={Settings2} title="Sales Defaults">
          <div className="pt-4 space-y-0">
            <FieldRow
              label="Payment Confirmation Required"
              hint="When ON, accepting a proposal creates a PaymentConfirmation (pending UTR). Accounts must confirm before the Sales Order is created."
            >
              <Toggle
                value={config.paymentConfirmationRequired}
                onChange={v => setConfig(c => ({ ...c, paymentConfirmationRequired: v }))}
              />
            </FieldRow>

            <FieldRow
              label="Additional Discount Cap (%)"
              hint="Maximum invoice-level additional discount Sales can apply before a DiscountRequest is triggered."
            >
              <NumberInput
                value={config.additionalDiscountMaxPct}
                onChange={v => setConfig(c => ({ ...c, additionalDiscountMaxPct: v }))}
                min={0}
                max={100}
              />
            </FieldRow>

            <FieldRow
              label="Max Missed Follow-Ups"
              hint="After this many consecutive unanswered calls, the lead is automatically marked Lost (reason: dead)."
            >
              <NumberInput
                value={config.maxMissedFollowUps}
                onChange={v => setConfig(c => ({ ...c, maxMissedFollowUps: v }))}
                min={1}
                max={20}
              />
            </FieldRow>
          </div>
        </SectionCard>

        {/* ── 3. Discount Approvals ─────────────────────────────────────────── */}
        <SectionCard icon={Percent} title="Discount Approvals">
          <div className="pt-4 space-y-0">
            <FieldRow
              label="Delegate to Accounts Head"
              hint="When ON, users in the Accounts department (role or dept name contains 'accounts') also see discount approval requests."
            >
              <Toggle
                value={config.discountApprovalDelegateToAccounts}
                onChange={v => setConfig(c => ({ ...c, discountApprovalDelegateToAccounts: v }))}
              />
            </FieldRow>
          </div>
        </SectionCard>

        {/* ── 4. Activity Tracking ──────────────────────────────────────────── */}
        <SectionCard icon={Activity} title="Activity Tracking">
          <div className="pt-4 space-y-0">
            <FieldRow
              label="Idle Timeout (minutes)"
              hint="Minutes of keyboard/mouse inactivity before the system records an idle segment. Affects SLA clock accuracy."
            >
              <NumberInput
                value={config.activityInactivityMinutes}
                onChange={v => setConfig(c => ({ ...c, activityInactivityMinutes: v }))}
                min={1}
                max={60}
              />
            </FieldRow>
          </div>
        </SectionCard>

        {/* ── 5. Role Permissions ───────────────────────────────────────────── */}
        <SectionCard icon={Shield} title="Role Permissions">
          <div className="pt-4 space-y-0">
            <FieldRow
              label="Team Head — Skip Payment Confirmation"
              hint="When ON, users with a Team Head role can accept a proposal and create the Sales Order directly, bypassing the UTR confirmation step."
            >
              <Toggle
                value={config.roleSettings.teamHead.canSkipPaymentConfirmation}
                onChange={v =>
                  setConfig(c => ({
                    ...c,
                    roleSettings: {
                      ...c.roleSettings,
                      teamHead: { ...c.roleSettings.teamHead, canSkipPaymentConfirmation: v },
                    },
                  }))
                }
              />
            </FieldRow>
          </div>
        </SectionCard>

        {/* ── 6. Field Location Tracking (LOC-D) ───────────────────────────── */}
        <SectionCard icon={MapPin} title="Field Location Tracking">
          <div className="pt-4 space-y-0">

            {/* 6-1 Master toggle */}
            <FieldRow
              label="Track field executive locations during work hours"
              hint="When enabled, AURA Sales mobile records GPS pings at the configured interval. Reps see a persistent 'Tracking active' notification on their phone."
            >
              <Toggle
                value={config.locationTrackingEnabled}
                onChange={v => setLoc({ locationTrackingEnabled: v })}
              />
            </FieldRow>

            {/* 6-1b Samsung Galaxy A07 fleet hint — static, no state */}
            {config.locationTrackingEnabled && (
              <div
                className="my-3 rounded-lg px-4 py-3 text-sm"
                style={{
                  background: 'var(--ivory-1)',
                  border: '1px solid var(--line)',
                  color: 'var(--ink-1)',
                }}
              >
                <p className="font-semibold mb-1.5" style={{ color: 'var(--ink-0)' }}>
                  Samsung Galaxy A07 fleet?
                </p>
                <p className="mb-2" style={{ color: 'var(--ink-2)' }}>
                  If you&apos;re deploying on Samsung Galaxy A07 phones (entry-level, 4&nbsp;GB RAM,
                  MediaTek Helio G99), set ping interval to <strong>20 minutes</strong> instead
                  of 15. Reasons:
                </p>
                <ul className="space-y-1 list-none pl-0" style={{ color: 'var(--ink-2)' }}>
                  <li>
                    <span className="font-semibold">Lower-end chip</span> = roughly 3% more
                    battery drain per day at 15-min interval
                  </li>
                  <li>
                    <span className="font-semibold">20-min interval still catches every site visit</span>{' '}
                    (reps stay 30+ min on average)
                  </li>
                  <li>
                    <span className="font-semibold">Samsung&apos;s One UI</span> is more aggressive about
                    killing background services on entry-level hardware — fewer pings = fewer
                    chances to be killed
                  </li>
                </ul>
                <p className="mt-2" style={{ color: 'var(--ink-3)' }}>
                  If you&apos;re on a fleet of mid-range phones (Motorola G54, Samsung M14, etc.),
                  15 min is fine.
                </p>
              </div>
            )}

            {/* 6-2 Energy-cost warning — only visible when tracking is ON */}
            {config.locationTrackingEnabled && (
              <div
                className="my-3 rounded-lg px-4 py-3 text-sm"
                style={{
                  background: 'var(--warning-bg, #FEF3C7)',
                  borderLeft: '3px solid var(--warning, #D97706)',
                  color: 'var(--ink-1)',
                }}
              >
                <p className="font-semibold mb-1.5" style={{ color: '#92400E' }}>
                  ⚠ Energy notice — read before deploying to a fleet
                </p>
                <p className="mb-2">Location tracking drains battery. The trade-off:</p>
                <ul className="space-y-1 list-none pl-0">
                  <li><span className="font-semibold">30 min interval:</span> ~3–5% extra battery/day. Coarse trail; misses short site visits.</li>
                  <li><span className="font-semibold">15 min interval</span> (recommended): ~6–9% extra battery/day. Good balance for sales/field execs.</li>
                  <li><span className="font-semibold">10 min interval:</span> ~10–14% extra battery/day. Use for delivery/installation crews.</li>
                  <li><span className="font-semibold">5 min interval:</span> ~18–25% extra battery/day. Rep will need a midday charge. Only use for compliance-critical roles.</li>
                </ul>
                <p className="mt-2" style={{ color: '#78350F' }}>
                  AURA's intelligent gating (skips when stationary, off-hours, low battery) cuts these numbers by ~40%, but you should still test on one device for a full day before rolling out to your fleet.
                </p>
              </div>
            )}

            {/* 6-3 Ping interval segmented control */}
            <FieldRow
              label={`Ping every ${config.locationPingIntervalMinutes} minutes`}
              hint="How often AURA records a GPS fix while the rep is active."
            >
              <div className="flex gap-1 flex-wrap justify-end">
                {PING_INTERVAL_OPTIONS.map(opt => {
                  const active = config.locationPingIntervalMinutes === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setLoc({ locationPingIntervalMinutes: opt })}
                      className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors"
                      style={{
                        background: active ? 'var(--saffron)' : 'var(--ivory-2, #F3EFE8)',
                        color: active ? 'white' : 'var(--ink-2)',
                        minWidth: '2.25rem',
                      }}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </FieldRow>

            {/* 6-4 Active hours only toggle */}
            <FieldRow
              label="Only track during company working hours"
              hint="Working hours are configured in the Working Hours section above. Outside those hours, AURA stops pinging — even if the rep is logged in."
            >
              <Toggle
                value={config.locationTrackingActiveHoursOnly}
                onChange={v => setLoc({ locationTrackingActiveHoursOnly: v })}
              />
            </FieldRow>

            {/* 6-5 Role multi-select */}
            <div className="py-3 border-b" style={{ borderColor: 'var(--line)' }}>
              <div className="flex items-start justify-between gap-4 mb-2">
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--ink-1)' }}>Roles to track</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--ink-3)' }}>
                    Empty = no one tracked. Most teams pick SALES_EXECUTIVE + FIELD_PARTNER.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {availableRoles.map(role => {
                  const selected = config.locationTrackingRoles.includes(role);
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() => toggleLocRole(role)}
                      className="px-2.5 py-1 rounded-full text-xs font-medium transition-colors"
                      style={{
                        background: selected ? 'var(--saffron)' : 'var(--ivory-2, #F3EFE8)',
                        color: selected ? 'white' : 'var(--ink-2)',
                        border: selected ? '1px solid var(--saffron)' : '1px solid var(--line)',
                      }}
                    >
                      {role}
                    </button>
                  );
                })}
              </div>
              {config.locationTrackingRoles.length === 0 && (
                <p className="text-xs mt-2" style={{ color: '#D97706' }}>
                  No roles selected — tracking is effectively off even if the master toggle is on.
                </p>
              )}
            </div>

            {/* 6-6 Stationary radius */}
            <FieldRow
              label="Skip pings when stationary (radius)"
              hint="If the rep hasn't moved beyond this radius for 3 consecutive pings, AURA stretches the next interval to save battery. 75m covers a typical office or small site."
            >
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={config.locationStationaryRadiusMeters}
                  min={10}
                  max={500}
                  onChange={e => setLoc({ locationStationaryRadiusMeters: Math.max(10, Math.min(500, Number(e.target.value))) })}
                  className="w-20 rounded-lg border px-2 py-1.5 text-sm text-right"
                  style={{
                    borderColor: 'var(--line)',
                    background: 'var(--ivory-0)',
                    color: 'var(--ink-1)',
                  }}
                />
                <span className="text-xs" style={{ color: 'var(--ink-3)' }}>metres</span>
              </div>
            </FieldRow>

            {/* 6-7 Per-section save */}
            <div className="pt-4 flex items-center justify-end gap-3">
              {locSaved && (
                <span className="text-xs flex items-center gap-1" style={{ color: '#16A34A' }}>
                  <CheckCircle2 size={13} /> Saved
                </span>
              )}
              <button
                type="button"
                onClick={handleLocSave}
                disabled={locSaving}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-opacity disabled:opacity-60"
                style={{ background: 'var(--saffron)' }}
              >
                {locSaving ? (
                  <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                ) : (
                  <Save size={14} />
                )}
                {locSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Bottom save bar */}
      <div className="fixed bottom-0 left-0 right-0 border-t py-3 px-6 flex justify-end" style={{ background: 'var(--ivory-0)', borderColor: 'var(--line)', zIndex: 40 }}>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-opacity disabled:opacity-60"
          style={{ background: 'var(--saffron)' }}
        >
          {saving ? (
            <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
          ) : saved ? (
            <CheckCircle2 size={16} />
          ) : (
            <Save size={16} />
          )}
          {saving ? 'Saving…' : saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

export default AdminSettingsPage;
